import { Component, Inject, inject, signal } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { AuthService } from "@app/services/auth.service";
import {FormBuilder, FormGroup, ReactiveFormsModule, Validators} from "@angular/forms";
import { SocialLoginComponent } from "@app/components/auth/social-login/social-login.component";
import { CommonModule } from '@angular/common';
import { RegisterLoginComponent } from '../register-login/register-login.component';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { finalize } from 'rxjs';
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import { DownloadLinksComponent } from '@app/components/download-links/download-links.component';
import { AlertComponent } from '@app/components/alert/alert.component';

@Component({
  selector: 'app-verify',
  standalone: true,
  imports: [CommonModule, MatFormFieldModule, MatInputModule, MatButtonModule, SocialLoginComponent, ReactiveFormsModule, MatProgressSpinnerModule, DownloadLinksComponent, AlertComponent],
  templateUrl: './verify.component.html',
  styleUrl: './verify.component.scss'
})
export class VerifyComponent {
  fb = inject(FormBuilder)
  authService = inject(AuthService)
  verifyForm: FormGroup;
  dialogRef = inject(MatDialogRef<RegisterLoginComponent>);
  isLoading = signal(false);
  isResendButtonDisabled = signal(false);
  remainingTime = 0;
  formattedTime = signal('00:00');

  successMessage = '';
  errorMessage = '';

  constructor(
    private dialog: MatDialog,
    @Inject(MAT_DIALOG_DATA) public data: { type: string; email?: string; password?: string },
  ) {
    this.verifyForm = this.fb.group({
      code: ['', [Validators.required, Validators.minLength(6), Validators.maxLength(6)]],
    });
    dayjs.extend(duration);
  }

  /** Email being verified — from the register step (data) or the current session. */
  private get email(): string | undefined {
    return this.data?.email ?? this.authService.userSignal()?.email ?? undefined;
  }

  verify(): void {
    if (this.verifyForm.valid) {
      this.isLoading.set(true);

      this.successMessage = '';
      this.errorMessage = '';

      const userEmail = this.email;
      const password = this.data?.password;
      const { code } = this.verifyForm.value;
      this.authService.verifyCode(userEmail, code)
        .pipe(
          finalize(() => {
            this.isLoading.set(false);
          })
        )
        .subscribe({
          next: () => {
            this.successMessage = 'User verified';
            // Backend register created the Firebase user but did NOT sign the
            // client in, so sign in now with the password from the register step.
            // Reload afterwards so the app picks up the authenticated session.
            if (userEmail && password) {
              this.authService.login(userEmail, password).subscribe({
                next: () => { this.dialogRef.close(); window.location.reload(); },
                error: () => { this.dialogRef.close(); window.location.reload(); },
              });
            } else {
              this.dialogRef.close();
              window.location.reload();
            }
          },
          error: () => {
            this.errorMessage = 'Invalid verification code';
          },
        });
    }
  }

  resendVerificationCode() {
    this.successMessage = 'User verification code sent';
    this.errorMessage = '';
    this.authService.resendVerificationCode(this.email).subscribe();
    this.disableResendButtonForTwoMinutes();
  }

  disableResendButtonForTwoMinutes() {
    this.isResendButtonDisabled.set(true);

    // Set remaining time to 120 seconds (2 minutes)
    this.remainingTime = 120;
    this.formattedTime.set(dayjs.duration(this.remainingTime, 'seconds').format('mm:ss'));

    const intervalId = setInterval(() => {
      if (this.remainingTime > 0) {
        this.remainingTime--;
        this.formattedTime.set(dayjs.duration(this.remainingTime, 'seconds').format('mm:ss'));
      } else {
        // Enable button and clear the interval when time is up
        this.isResendButtonDisabled.set(false);
        clearInterval(intervalId);
      }
    }, 1000);
  }

  onVerify(event: Event) {
    event.preventDefault();
    this.verify();
  }

  openSignInForm() {
    this.dialogRef.close();
    this.dialog.open(RegisterLoginComponent, {
      maxWidth: '1000px',
      data: {
        type: 'login',
      },
    });
  }
}
